/*
  Где открыть пояснение значка «i» (02.10.2026, указание владельца: «обычно
  отображать сверху справа, если места не хватает - тогда слева и т. д.»).

  Порядок - тот, что принят для всплывающих подсказок везде (так работает
  Floating UI, на нём Radix и Mantine): желаемое положение и запасные,
  первое, куда панель помещается ЦЕЛИКОМ, выигрывает. Целиком никуда, но по
  высоте сверху или снизу помещается - эта сторона, в том же порядке, со
  сдвигом внутрь экрана по ширине. Не помещается и по высоте - панель
  открывается туда, где места больше, и сдвигается внутрь экрана.

  Сама библиотека не подключена: из неё здесь нужен ровно этот расчёт, а новая
  зависимость - это установка и в контейнер кабинета, и в сборку.

  🔴 Решение принимается по настоящему размеру панели, замеренному после
  отрисовки. До этой правки высота была угадана числом, панель открывалась вниз
  почти всегда и у нижнего края уходила за окно.

  Чистая функция от трёх прямоугольников - поэтому проверяется без браузера
  (`__tests__/hint_placement.test.js`).
*/

// Между значком и панелью.
export const GAP = 8
// Поле от края окна: панель вплотную к краю читается обрезанной.
export const EDGE = 16

// «top-start» - над значком, от его левого края вправо; «end» - от правого
// края влево.
const ORDER = ['top-start', 'top-end', 'bottom-start', 'bottom-end']

function spotFor(placement, anchor, panel) {
  const [side, align] = placement.split('-')
  return {
    left: align === 'start' ? anchor.left : anchor.right - panel.width,
    top: side === 'top' ? anchor.top - GAP - panel.height : anchor.bottom + GAP,
  }
}

const fitsHeight = (top, panel, view) => top >= EDGE && top + panel.height <= view.height - EDGE

function fits({ left, top }, panel, view) {
  return left >= EDGE && left + panel.width <= view.width - EDGE && fitsHeight(top, panel, view)
}

const clamp = (value, low, high) => Math.max(low, Math.min(value, high))

const shiftInside = (left, panel, view) => clamp(left, EDGE, view.width - panel.width - EDGE)

export function placeHint(anchor, panel, view) {
  for (const placement of ORDER) {
    const spot = spotFor(placement, anchor, panel)
    if (fits(spot, panel, view)) return { ...spot, placement }
  }
  /*
    Целиком не встала нигде, но по высоте сторона может подойти - так бывает,
    когда панель шире места и справа, и слева от значка (телефон, узкое окно,
    увеличение в браузере). Тогда сторона берётся в том же порядке, сначала
    сверху, а нехватка ширины закрывается сдвигом внутрь экрана: так и делает
    Floating UI - переворот по высоте, сдвиг по ширине.

    До 05.10.2026 этого шага не было, и сторона сразу выбиралась по тому, где
    места больше, - а снизу его почти всегда больше, поэтому на узком окне
    панель уходила вниз, хотя сверху помещалась (замечание владельца).
  */
  for (const placement of ['top-start', 'bottom-start']) {
    const spot = spotFor(placement, anchor, panel)
    if (fitsHeight(spot.top, panel, view)) {
      return { left: shiftInside(spot.left, panel, view), top: spot.top, placement }
    }
  }
  const roomAbove = anchor.top
  const roomBelow = view.height - anchor.bottom
  const placement = roomAbove >= roomBelow ? 'top-start' : 'bottom-start'
  const spot = spotFor(placement, anchor, panel)
  return {
    left: shiftInside(spot.left, panel, view),
    top: clamp(spot.top, EDGE, view.height - panel.height - EDGE),
    placement,
  }
}
